/************************************************************************************

CONSTANTS / PUBSUB EVENTS

*************************************************************************************/
window.ON_CHANGE_DEBOUNCE_TIMER = 300

window.PUB_SUB_EVENTS = {
	cartUpdate: 'cart-update',
	quantityUpdate: 'quantity-update',
	optionValueSelectionChange: 'option-value-selection-change',
	variantChange: 'variant-change',
	cartError: 'cart-error',
	// Custom events
	library: {
		carousel: 'carousel-loaded',
	},
	events: {
		resize: 'window-resize',
		matchMedia: 'window-match-media',
		click: 'window-click',
		keyup: 'window-keyup',
		componentResize: 'component-resize',
	},
}

let subscribers = {}

function subscribe(eventName, callback) {
	if (subscribers[eventName] === undefined) {
		subscribers[eventName] = []
	}

	subscribers[eventName] = [...subscribers[eventName], callback]

	return function unsubscribe() {
		subscribers[eventName] = subscribers[eventName].filter((cb) => {
			return cb !== callback
		})
	}
}

function publish(eventName, data) {
	if (subscribers[eventName]) {
		subscribers[eventName].forEach((callback) => {
			callback(data)
		})
	}
}

/************************************************************************************

UTILITIES FUNCTIONS

*************************************************************************************/
function formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function initLazyScript(element, callback, threshold = 500) {
	if ('IntersectionObserver' in window) {
		const io = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (typeof callback === 'function') {
							callback()
							observer.unobserve(entry.target)
						}
					}
				})
			},
			{ rootMargin: `0px 0px ${threshold}px 0px` }
		)

		io.observe(element)
	} else {
		callback()
	}
}

function getFocusableElements(container) {
	const focusableElements = container?.querySelectorAll(
		"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
	)
	return Array.from(focusableElements || [])
}

class SectionId {
	static #separator = '__'

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
	static parseId(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[0]
	}

	// for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
	static parseSectionName(qualifiedSectionId) {
		return qualifiedSectionId.split(SectionId.#separator)[1]
	}

	// for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
	static getIdForSection(sectionId, sectionName) {
		return `${sectionId}${SectionId.#separator}${sectionName}`
	}
}

class HTMLUpdateUtility {
	/**
	 * Used to swap an HTML node with a new node.
	 * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
	 *
	 * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
	 */
	static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
		preProcessCallbacks?.forEach((callback) => callback(newContent))

		const newNodeWrapper = document.createElement('div')
		HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
		const newNode = newNodeWrapper.firstChild

		// dedupe IDs
		const uniqueKey = Date.now()
		oldNode.querySelectorAll('[id], [form]').forEach((element) => {
			element.id && (element.id = `${element.id}-${uniqueKey}`)
			element.form && element.classList.add('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
		})

		oldNode.parentNode.insertBefore(newNode, oldNode)
		oldNode.style.display = 'none'

		postProcessCallbacks?.forEach((callback) => callback(newNode))

		setTimeout(() => oldNode.remove(), 500)
	}

	// Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
	static setInnerHTML(element, html) {
		element.innerHTML = html
		element.querySelectorAll('script').forEach((oldScriptTag) => {
			const newScriptTag = document.createElement('script')
			Array.from(oldScriptTag.attributes).forEach((attribute) => {
				newScriptTag.setAttribute(attribute.name, attribute.value)
			})
			newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
			oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
		})
	}
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
	summary.setAttribute('role', 'button')
	summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'))

	if (summary.nextElementSibling.getAttribute('id')) {
		summary.setAttribute('aria-controls', summary.nextElementSibling.id)
	}

	summary.addEventListener('click', (event) => {
		event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
	})

	if (summary.closest('header-drawer, menu-drawer')) return
	summary.parentElement.addEventListener('keyup', onKeyUpEscape)
})

const trapFocusHandlers = {}

function trapFocus(container, elementToFocus = container) {
	var elements = getFocusableElements(container)
	var first = elements[0]
	var last = elements[elements.length - 1]

	removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (event.target !== container && event.target !== last && event.target !== first) return

		document.addEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || event.target === first) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus.focus()

	if (
		elementToFocus.tagName === 'INPUT' &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length)
	}
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
	document.querySelector(':focus-visible')
} catch (e) {
	focusVisiblePolyfill()
}

function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	]
	let currentFocusedElement = null
	let mouseClick = null

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code.toUpperCase())) {
			mouseClick = false
		}
	})

	window.addEventListener('mousedown', (event) => {
		mouseClick = true
	})

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement) currentFocusedElement.classList.remove('focused')

			if (mouseClick) return

			currentFocusedElement = document.activeElement
			currentFocusedElement.classList.add('focused')
		},
		true
	)
}

function pauseAllMedia() {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document.querySelectorAll('video').forEach((video) => {
		video.pause()
		video.parentElement.classList.add('is-video-paused')
	})
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
}

function removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', trapFocusHandlers.focusin)
	document.removeEventListener('focusout', trapFocusHandlers.focusout)
	document.removeEventListener('keydown', trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape(event) {
	if (event.code.toUpperCase() !== 'ESCAPE') return

	const openDetailsElement = event.target.closest('details[open]')
	if (!openDetailsElement) return

	const summaryElement = openDetailsElement.querySelector('summary')
	openDetailsElement.removeAttribute('open')
	summaryElement.setAttribute('aria-expanded', false)
	summaryElement.focus()
}

class QuantityInput extends HTMLElement {
	constructor() {
		super()
		this.input = this.querySelector('input')
		this.changeEvent = new Event('change', { bubbles: true })
		this.input.addEventListener('change', this.onInputChange.bind(this))
		this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)))
	}

	quantityUpdateUnsubscriber = undefined

	connectedCallback() {
		this.validateQtyRules()
		this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))
	}

	disconnectedCallback() {
		if (this.quantityUpdateUnsubscriber) {
			this.quantityUpdateUnsubscriber()
		}
	}

	onInputChange(event) {
		this.validateQtyRules()
	}

	onButtonClick(event) {
		event.preventDefault()
		const previousValue = this.input.value

		if (event.target.name === 'plus') {
			if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
				this.input.value = this.input.dataset.min
			} else {
				this.input.stepUp()
			}
		} else {
			this.input.stepDown()
		}

		if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

		if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
			this.input.value = parseInt(this.input.min)
		}
	}

	validateQtyRules() {
		const value = parseInt(this.input.value)
		if (this.input.min) {
			const buttonMinus = this.querySelector(".quantity__button[name='minus']")
			buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
		}
		if (this.input.max) {
			const max = parseInt(this.input.max)
			const buttonPlus = this.querySelector(".quantity__button[name='plus']")
			buttonPlus.classList.toggle('disabled', value >= max)
		}
	}
}

customElements.define('quantity-input', QuantityInput)

function debounce(fn, wait) {
	let t
	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function throttle(fn, delay) {
	let lastCall = 0
	return function (...args) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now
		return fn(...args)
	}
}

function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

function updateHeaderCSSVariables() {
	var headerSection = headerSection || document.querySelector('.l-header')
	var announcementBarSection = announcementBarSection || document.querySelector('.section-announcement-bar')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${headerSection.scrollHeight}px`)
	document.documentElement.style.setProperty('--l-announcement-bar-height', `${announcementBarSection.scrollHeight}px`)
}

function lockBodyScroll() {
	document.documentElement.classList.add('js-block-page')
}

function unLockBodyScroll() {
	document.documentElement.classList.remove('js-block-page')
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
	window.Shopify = {}
}

Shopify.bind = function (fn, scope) {
	return function () {
		return fn.apply(scope, arguments)
	}
}

Shopify.setSelectorByValue = function (selector, value) {
	for (var i = 0, count = selector.options.length; i < count; i++) {
		var option = selector.options[i]
		if (value == option.value || value == option.innerHTML) {
			selector.selectedIndex = i
			return i
		}
	}
}

Shopify.addListener = function (target, eventName, callback) {
	target.addEventListener
		? target.addEventListener(eventName, callback, false)
		: target.attachEvent('on' + eventName, callback)
}

Shopify.postLink = function (path, options) {
	options = options || {}
	var method = options['method'] || 'post'
	var params = options['parameters'] || {}

	var form = document.createElement('form')
	form.setAttribute('method', method)
	form.setAttribute('action', path)

	for (var key in params) {
		var hiddenField = document.createElement('input')
		hiddenField.setAttribute('type', 'hidden')
		hiddenField.setAttribute('name', key)
		hiddenField.setAttribute('value', params[key])
		form.appendChild(hiddenField)
	}
	document.body.appendChild(form)
	form.submit()
	document.body.removeChild(form)
}

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
	this.countryEl = document.getElementById(country_domid)
	this.provinceEl = document.getElementById(province_domid)
	this.provinceContainer = document.getElementById(options['hideElement'] || province_domid)

	Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this))

	this.initCountry()
	this.initProvince()
}

Shopify.CountryProvinceSelector.prototype = {
	initCountry: function () {
		var value = this.countryEl.getAttribute('data-default')
		Shopify.setSelectorByValue(this.countryEl, value)
		this.countryHandler()
	},

	initProvince: function () {
		var value = this.provinceEl.getAttribute('data-default')
		if (value && this.provinceEl.options.length > 0) {
			Shopify.setSelectorByValue(this.provinceEl, value)
		}
	},

	countryHandler: function (e) {
		var opt = this.countryEl.options[this.countryEl.selectedIndex]
		var raw = opt.getAttribute('data-provinces')
		var provinces = JSON.parse(raw)

		this.clearOptions(this.provinceEl)
		if (provinces && provinces.length == 0) {
			this.provinceContainer.style.display = 'none'
		} else {
			for (var i = 0; i < provinces.length; i++) {
				var opt = document.createElement('option')
				opt.value = provinces[i][0]
				opt.innerHTML = provinces[i][1]
				this.provinceEl.appendChild(opt)
			}

			this.provinceContainer.style.display = ''
		}
	},

	clearOptions: function (selector) {
		while (selector.firstChild) {
			selector.removeChild(selector.firstChild)
		}
	},

	setOptions: function (selector, values) {
		for (var i = 0, count = values.length; i < values.length; i++) {
			var opt = document.createElement('option')
			opt.value = values[i]
			opt.innerHTML = values[i]
			selector.appendChild(opt)
		}
	},
}

class ModalOpener extends HTMLElement {
	constructor() {
		super()

		const button = this.querySelector('button')

		if (!button) return
		button.addEventListener('click', () => {
			const modal = document.querySelector(this.getAttribute('data-modal'))
			if (modal) modal.show(button)
		})
	}
}
customElements.define('modal-opener', ModalOpener)

class DeferredMedia extends HTMLElement {
	constructor() {
		super()

		const poster = this.querySelector('[id^="Deferred-Poster-"]')
		if (!poster) return

		poster.addEventListener('click', this.loadContent.bind(this))
	}

	loadContent(focus = true) {
		window.pauseAllMedia()
		if (!this.getAttribute('loaded')) {
			const content = document.createElement('div')
			content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true))

			this.setAttribute('loaded', true)
			const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'))
			if (focus) deferredElement.focus()
			if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
				// force autoplay for safari
				deferredElement.play()
			}

			if (deferredElement.nodeName == 'VIDEO') {
				this.videoEvents(deferredElement)
			}
		}
	}

	videoEvents(deferredElement) {
		this.deferredElement = deferredElement
		this.muteButton = this.querySelector('[id^="Deferred-Mute-"]')
		this.playButton = this.querySelector('[id^="Deferred-Play-"]')

		if (this.muteButton) {
			this.muteButton.addEventListener('click', () => {
				this.muteButton.classList.toggle('is-mute')
				this.deferredElement.muted = !this.muteButton.classList.contains('is-mute')
			})
		}

		if (this.playButton) {
			this.playButton.addEventListener('click', () => {
				console.log(this.deferredElement)
				window.pauseAllMedia()
				this.deferredElement.parentElement.classList.remove('is-video-paused')
				this.deferredElement.play()
			})
		}
	}
}

customElements.define('deferred-media', DeferredMedia)

class VariantSelects extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.addEventListener('change', (event) => {
			const target = this.getInputForEventTarget(event.target)
			this.updateSelectionMetadata(event)

			publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
				data: {
					event,
					target,
					selectedOptionValues: this.selectedOptionValues,
				},
			})
		})
	}

	updateSelectionMetadata({ target }) {
		const { value, tagName } = target

		if (tagName === 'SELECT' && target.selectedOptions.length) {
			Array.from(target.options)
				.find((option) => option.getAttribute('selected'))
				.removeAttribute('selected')
			target.selectedOptions[0].setAttribute('selected', 'selected')

			const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue
			const selectedDropdownSwatchValue = target
				.closest('.product-form__input')
				.querySelector('[data-selected-value] > .swatch')
			if (!selectedDropdownSwatchValue) return
			if (swatchValue) {
				selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue)
				selectedDropdownSwatchValue.classList.remove('swatch--unavailable')
			} else {
				selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset')
				selectedDropdownSwatchValue.classList.add('swatch--unavailable')
			}

			selectedDropdownSwatchValue.style.setProperty(
				'--swatch-focal-point',
				target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
			)
		} else if (tagName === 'INPUT' && target.type === 'radio') {
			const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]')
			if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
		}
	}

	getInputForEventTarget(target) {
		return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
	}

	get selectedOptionValues() {
      // console.log("adssss============================================= update_price_cstm_global_js ");
      try{
        setTimeout(function(){
          callEnableFunction()
        },1000)
      }catch(err){console.log("err_enblecart_8 ",err)}
		return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
			({ dataset }) => dataset.optionValueId
		)
	}
}

customElements.define('variant-selects', VariantSelects)

class ProductRecommendations extends HTMLElement {
	observer = undefined

	constructor() {
		super()
	}

	connectedCallback() {
		this.initializeRecommendations(this.dataset.productId)
	}

	initializeRecommendations(productId) {
		this.observer?.unobserve(this)
		this.observer = new IntersectionObserver(
			(entries, observer) => {
				if (!entries[0].isIntersecting) return
				observer.unobserve(this)
				this.loadRecommendations(productId)
			},
			{ rootMargin: '0px 0px 400px 0px' }
		)
		this.observer.observe(this)
	}

	loadRecommendations(productId) {
		fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
			.then((response) => response.text())
			.then((text) => {
				const html = document.createElement('div')
				html.innerHTML = text
				const recommendations = html.querySelector('product-recommendations')

				if (recommendations?.innerHTML.trim().length) {
					this.innerHTML = recommendations.innerHTML
				}

				if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
					this.remove()
				}

				if (html.querySelector('.grid__item')) {
					this.classList.add('product-recommendations--loaded')
				}
			})
			.catch((e) => {
				console.error(e)
			})
	}
}

customElements.define('product-recommendations', ProductRecommendations)

class AccountIcon extends HTMLElement {
	constructor() {
		super()

		this.icon = this.querySelector('.icon')
	}

	connectedCallback() {
		document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this))
	}

	handleStorefrontSignInCompleted(event) {
		if (event?.detail?.avatar) {
			this.icon?.replaceWith(event.detail.avatar.cloneNode())
		}
	}
}

customElements.define('account-icon', AccountIcon)

/************************************************************************************

l-collapsible-tab

*************************************************************************************/
class CollapsibleTab extends HTMLElement {
	constructor() {
		super()

		window.initLazyScript(this, this.init.bind(this))
	}

	disconnectedCallback() {
		this.destroy()
	}

	init() {
		this.setDefaultData()
		this.attachEvents()

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.classList.add(this.expandedClass)
			this.setExpandedAria()
		} else {
			this.content.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria()
		}

		this.content.removeAttribute('hidden')
	}

	setDefaultData() {
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'

		this.transitionendEvent = this.transitionendEventName()

		this.trigger = this.querySelector('[data-trigger]')
		this.content = this.querySelector('[data-content]')

		this.collapsedHeight = '0px'
		this.defaultElements = ['a', 'button', 'input:not(.focus-none)', '[data-trigger]']
		this.oneAtATime = true

		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)
	}

	attachEvents() {
		this.trigger.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		if (e.target.tagName == 'A') return

		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-container]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()
		this.setAttribute('open', true)
	}

	close() {
		this.selected = false
		this.collapse()
		this.removeAttribute('open')
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.content.style.transition = '0'
						this.content.style.height = 'auto'

						requestAnimationFrame(() => {
							this.content.style.height = null
							this.content.style.transition = null
							this.setExpandedAria()
							this.trySetTabIndex(this.content, 0)
						})
					})
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		this.content.style.height = this.content.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.setCollapsedAria()
					this.classList.add(this.collapsedClass)
					this.trySetTabIndex(this.content, -1)
				}
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.content.style.transition = '0'
			this.content.style.height = this.content.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.content.style.transition = null
				this.content.style.height = this.collapsedHeight
			})
		})
	}

	destroy() {
		this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
		this.content && this.content.removeAttribute('aria-hidden')
		this.content && (this.content.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	trySetTabIndex(parentElement, tabIndex) {
		const elements = parentElement.querySelectorAll(this.defaultElements)

		elements &&
			elements.forEach((e) => {
				e.setAttribute('tabindex', tabIndex)
			})
	}

	setExpandedAria() {
		this.trigger.setAttribute('aria-expanded', 'true')
		this.content.setAttribute('aria-hidden', 'false')
	}

	setCollapsedAria() {
		this.trigger.setAttribute('aria-expanded', 'false')
		this.content.setAttribute('aria-hidden', 'true')
	}
}

customElements.define('l-collapsible-tab', CollapsibleTab)

/************************************************************************************

l-count-down

*************************************************************************************/
if (!customElements.get('l-count-down')) {
	class LCountDown extends HTMLElement {
		constructor() {
			super()

			this.endDate = new Date(this.dataset.endDate).getTime()

			if (!Number(this.endDate)) return

			window.initLazyScript(this, this.init.bind(this))
		}

		init() {
			this.daysEl = this.querySelector('.js-days')
			this.hoursEl = this.querySelector('.js-hours')
			this.minsEl = this.querySelector('.js-mins')
			this.secsEl = this.querySelector('.js-secs')

			this.second = 1000
			this.minute = 60 * this.second
			this.hour = 60 * this.minute
			this.day = 24 * this.hour

			this.timer()
			this.interval = setInterval(this.timer.bind(this), this.second)
		}

		timer() {
			const timeDiff = this.endDate - new Date()

			if (timeDiff < 0) {
				clearInterval(this.interval)

				return
			}

			const days = Math.floor(timeDiff / this.day)
			const hours = Math.floor(timeDiff / this.hour)
			const mins = Math.floor(timeDiff / this.minute)
			const secs = Math.floor(timeDiff / this.second)

			this.daysEl.textContent = days
			this.hoursEl.textContent = hours - days * 24
			this.minsEl.textContent = mins - hours * 60
			this.secsEl.textContent = secs - mins * 60 < 10 ? `0${secs - mins * 60}` : secs - mins * 60
		}
	}

	customElements.define('l-count-down', LCountDown)
}

/************************************************************************************

l-theme-config

*************************************************************************************/
if (!customElements.get('l-theme-config')) {
	class LTAThemeConfig extends HTMLElement {
		constructor() {
			super()

			this.init()
		}

		init() {
			this.onWindowScroll()
		}

		onWindowScroll() {
	const headerGroup = document.getElementById('HeaderGroup')
	const headerSection = document.querySelector('.l-header')
	const announcementBarSection = document.querySelector('.section-announcement-bar')

	if (!headerGroup) return

	let lastScrollY = window.scrollY
	let ticking = false
	// The first scroll event after load (incl. the browser's scroll-restoration on
	// refresh) only syncs lastScrollY — it must NOT be read as "scrolled down", or a
	// mid-page refresh would flash the header then hide it.
	let primed = false

	// Use requestAnimationFrame, not a trailing debounce. A debounce only fires once
	// scrolling fully stops; on a mobile momentum flick that means the up-scroll reveal
	// doesn't trigger until the flick settles (near the top). rAF runs every frame while
	// scrolling, so hide/reveal track the gesture immediately on mobile and desktop alike.
	const update = () => {
		ticking = false
		const currentScrollY = window.scrollY

		if (!primed) {
			primed = true
			lastScrollY = currentScrollY
			return
		}

		const headerHeight =
			(headerSection ? headerSection.scrollHeight : 0) +
			(announcementBarSection ? announcementBarSection.scrollHeight : 0)

		// Scrolled down past the header height — hide
		if (currentScrollY > lastScrollY + 2 && currentScrollY > headerHeight) {
			headerGroup.classList.add('is-hidden')
		}
		// Scrolled up — reveal
		else if (currentScrollY < lastScrollY - 2) {
			headerGroup.classList.remove('is-hidden')
		}

		lastScrollY = currentScrollY
	}

	window.addEventListener(
		'scroll',
		() => {
			if (!ticking) {
				window.requestAnimationFrame(update)
				ticking = true
			}
		},
		{ passive: true }
	)
}

	}

	customElements.define('l-theme-config', LTAThemeConfig)
}

/************************************************************************************

l-featured-collections

*************************************************************************************/
if (!customElements.get('l-section-featured-products')) {
	customElements.define(
		'l-section-featured-products',
		class SectionFeaturedProducts extends HTMLElement {
			constructor() {
				super()
			}

			carouselLibraryLoaded = undefined

			connectedCallback() {
				this.carouselLibraryLoaded = subscribe(window.PUB_SUB_EVENTS.library.carousel, () => {
					this.setupCarousel()
				})
			}

			disconnectedCallback() {
				if (this.carouselLibraryLoaded) {
					this.carouselLibraryLoaded()
				}
			}

			setupCarousel() {
				this.sectionId = this.dataset.sectionId
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('hidden'))

				this.swiperCarousel = new window.Swiper(this, {
					slidesPerView: 'auto',
					loop: false,
					pagination: {
						el: `#Pagination-${this.sectionId}`,
						clickable: true,
					},
					breakpoints: {
						990: {
							centeredSlides: false,
						},
					},
				})
			}
		}
	)
}

/************************************************************************************

l-swiper-carousel

*************************************************************************************/
if (!customElements.get('l-swiper-carousel')) {
	customElements.define(
		'l-swiper-carousel',
		class SwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			carouselLibraryLoaded = undefined

			connectedCallback() {
				this.carouselLibraryLoaded = subscribe(window.PUB_SUB_EVENTS.library.carousel, () => {
					this.setupCarousel()
				})
			}

			disconnectedCallback() {
				if (this.carouselLibraryLoaded) {
					this.carouselLibraryLoaded()
				}
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			}
		}
	)
}

/************************************************************************************

l-header-mobile

*************************************************************************************/
if (!customElements.get('l-header-mobile')) {
	customElements.define(
		'l-header-mobile',
		class LTAHeaderMobile extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				this.sectionId = this.dataset.sectionId

				// Elements
				this.menuToggleEl = this.querySelector('[data-menu-toggle]')
				this.meuDrawerEl = document.querySelector(`#headerMobile-${this.sectionId}`)

				this.resizeDebounce = window.debounce(() => {
					updateHeaderCSSVariables()
				}, 50)

				// Setup events
				this.setOpenMenu()
				this.setupOpenSubmenu()
				this.setupBackSubmenu()
			}

			disconnectedCallback() {}

			setOpenMenu() {
				this.menuToggleEl.addEventListener('click', () => {
					const isOpen = this.menuToggleEl.classList.contains('is-active')

					this.menuToggleEl.classList.toggle('is-active', !isOpen)

					isOpen ? this.close() : this.open()
				})
			}

			setupOpenSubmenu() {
				this.layerTriggerEls = this.meuDrawerEl.querySelectorAll('[data-layer-trigger]')
				this.layerEls = this.meuDrawerEl.querySelectorAll('[data-second-layer]')

				this.layerTriggerEls.forEach((layerTriggerEl) => {
					layerTriggerEl.addEventListener('click', (event) => {
						event.preventDefault()

						const triggerId = layerTriggerEl.dataset.layerTrigger

						this.layerEls.forEach((layerEl) => {
							const layerId = layerEl.dataset.secondLayer
							layerId == triggerId ? this.openSubmenu(layerEl) : this.closeSubmenu(layerEl)
						})
					})
				})
			}

			setupBackSubmenu() {
				this.layerBackEls = this.meuDrawerEl.querySelectorAll('[data-layer-back]')

				this.layerBackEls.forEach((layerBackEl) => {
					layerBackEl.addEventListener('click', () => {
						const secondLayer = layerBackEl.parentElement

						this.closeSubmenu(layerBackEl.parentElement)
					})
				})
			}

			openSubmenu(layerEl) {
				if (!layerEl) return

				layerEl.classList.remove('hidden')

				requestAnimationFrame(() => {
					layerEl.classList.add('is-active')
				})
			}

			closeSubmenu(layerEl) {
				if (!layerEl) return

				layerEl.classList.remove('is-active')

				window.debounce(() => {
					layerEl.classList.add('hidden')
				}, 400)()
			}

			open() {
				lockBodyScroll()

				// Add classes to open drawer
				this.meuDrawerEl.classList.remove('hidden')

				requestAnimationFrame(() => {
					this.meuDrawerEl.classList.add('is-active')
				})

				window.debounce(() => {
					this.meuDrawerEl.classList.add('is-showed')
				}, 400)()

				// Setup resize event
				updateHeaderCSSVariables()

				window.addEventListener('resize', this.resizeDebounce)
				this.meuDrawerEl.addEventListener('click', this.onClickOutsideEvent.bind(this))
				window.addEventListener('keyup', this.onKeyUpToClose.bind(this))
			}

			close() {
				this.meuDrawerEl.classList.remove('is-showed')
				this.meuDrawerEl.classList.remove('is-active')

				console.log('Header Close')

				window.debounce(() => {
					this.menuToggleEl.classList.remove('is-active')
					this.meuDrawerEl.classList.remove('hidden')

					window.removeEventListener('resize', this.resizeDebounce)
					this.meuDrawerEl.removeEventListener('click', this.onClickOutsideEvent)
					window.removeEventListener('keyup', this.onKeyUpToClose)

					unLockBodyScroll()
				}, 400)()
			}

			closeWithoutLockBody() {
				this.menuToggleEl.classList.remove('is-active')
				this.meuDrawerEl.classList.remove('hidden')

				window.removeEventListener('resize', this.resizeDebounce)
				this.meuDrawerEl.removeEventListener('click', this.onClickOutsideEvent)
				window.removeEventListener('keyup', this.onKeyUpToClose)
			}

			onClickOutsideEvent(event) {
				const isOpen = this.meuDrawerEl.classList.contains('is-active')
				if (!isOpen) return

				const target = event.target
				const validParentElements = target.closest('[data-layer-level-1], [data-second-layer]')

				if (!validParentElements) this.close()
			}

			onKeyUpToClose(event) {
				if (`${event.key}`.toUpperCase() == 'ESCAPE') this.close()
			}
		}
	)
}

/************************************************************************************

l-header-search-bar

*************************************************************************************/
if (!customElements.get('l-header-search-bar')) {
	customElements.define(
		'l-header-search-bar',
		class LTASearchBar extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				this.cachedResults = {}
				this.abortController = new AbortController()

				// Elements
				this.headers = document.querySelectorAll('l-header-mobile')
				this.fakeInputEl = document.querySelector('[data-search-input-fake]')
				this.searchFormEl = this.querySelector('[data-search-form]')
				this.searchInputEl = this.querySelector('[data-search-input]')
				this.searchDrawerEl = this.querySelector('[data-search-drawer]')
				this.searchClearBtnEl = this.querySelector('[data-search-clear-btn]')
				this.searchContentEl = this.querySelector('[data-predictive-search-content]')
				this.suggestionsEl = this.querySelector('[data-suggestions]')
				this.openSearchBtnEls = document.querySelectorAll('[data-header-search-trigger]')
				this.closeSearchEl = this.querySelector('[data-search-clear-btn')

				// Functions
				this.resizeDebounce = window.debounce(() => {
					updateHeaderCSSVariables()
				}, 50)

				// Events
				// this.searchFormEl.addEventListener('submit', this.setupSearchResults.bind(this))
				this.searchInputEl.addEventListener(
					'input',
					window.debounce((event) => {
						this.setupSearchResults(event)
					}, 50)
				)

				// Setup open / close events
				this.setToggleEvents()

				// Setup desktop position
				this.setPositionRightDesktop()
			}

			setupSearchResults(event) {
				event.preventDefault()

				const searchTerm = this.searchInputEl.value
				const queryKey = searchTerm.replace(' ', '-').toLowerCase()

				if (!searchTerm.trim()) {
					this.resetInEmptySearchTerms()

					return
				}

				if (this.cachedResults[queryKey]) {
					this.renderSearchResults(this.cachedResults[queryKey])

					return
				}

				fetch(
					`${routes.predictive_search_url}?q=${encodeURIComponent(this.searchInputEl.value)}&section_id=a-predictive-search`
				)
					.then((response) => {
						if (!response.ok) {
							throw new Error(response)
						}

						return response.text()
					})
					.then((text) => {
						const resultsMarkup = new DOMParser()
							.parseFromString(text, 'text/html')
							.querySelector('#shopify-section-a-predictive-search')

						// Save bandwidth keeping the cache in all instances synced
						this.cachedResults[queryKey] = resultsMarkup

						this.renderSearchResults(resultsMarkup)
					})
					.catch((error) => {
						console.error(error)
					})
			}

			renderSearchResults(resultsMarkup) {
				const isEmpty = resultsMarkup.querySelector('[data-predictive-search-empty-text]')
				this.suggestionsEl.classList.toggle('hidden', !isEmpty)

				this.searchContentEl.innerHTML = resultsMarkup.innerHTML
			}

			resetInEmptySearchTerms() {
				this.suggestionsEl.classList.remove('hidden')

				this.searchContentEl.innerHTML = ''
			}

			setToggleEvents() {
				this.openSearchBtnEls.forEach((openSearchBtnEl) => {
					openSearchBtnEl.addEventListener('click', () => {
						this.open()
					})
				})

				this.closeSearchEl.addEventListener('click', () => {
					this.close()
				})

				this.fakeInputEl.parentElement.addEventListener('click', () => {
					this.open()
				})
			}

			setPositionRightDesktop() {
				const scrollBarWidth = 15
				const positionLeft =
					window.innerWidth -
					(this.fakeInputEl.getBoundingClientRect().x + this.fakeInputEl.offsetWidth) -
					scrollBarWidth

				this.style.setProperty('--l-header-search-right', positionLeft + 'px')
			}

			open() {
				this.headers.forEach((header) => {
					header.closeWithoutLockBody()
				})

				// Setup resize event
				updateHeaderCSSVariables()
				this.setPositionRightDesktop()

				// Body scroll lock
				lockBodyScroll()

				// Add classes to open drawer
				this.classList.remove('hidden')

				requestAnimationFrame(() => {
					this.searchDrawerEl.classList.add('is-active')
				})

				// Focus search input
				this.searchInputEl.focus()

				// Setup resize events
				debounce(() => {
					window.addEventListener('click', this.onClickOutOfEl.bind(this))
					window.addEventListener('resize', this.resizeDebounce)
					window.addEventListener('keyup', this.onKeyUpToClose.bind(this))
				}, 100)()
			}

			close() {
				this.searchDrawerEl.classList.remove('is-active')

				window.debounce(() => {
					this.classList.remove('hidden')

					window.removeEventListener('click', this.onClickOutOfEl)
					window.removeEventListener('resize', this.resizeDebounce)
					window.removeEventListener('keyup', this.onKeyUpToClose)

					unLockBodyScroll()
				}, 400)()
			}

			onClickOutOfEl(event) {
				if (!this.contains(event.target)) {
					this.close()
				}
			}

			onKeyUpToClose(event) {
				if (`${event.key}`.toUpperCase() == 'ESCAPE') this.close()
			}
		}
	)
}

/************************************************************************************

l-header-desktop

*************************************************************************************/
if (!customElements.get('l-header-desktop')) {
	customElements.define(
		'l-header-desktop',
		class LTAHeaderDesktop extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.menuLinkEls = this.querySelectorAll('[data-menu-drawer-link]')
				this.menuDrawerEls = document.querySelectorAll('[id^="menuDesktop-"]')

				// Setup events
				this.setMenuDrawerPosition()
				this.setupOpenMenuOnHoverEvents()
				this.toggleCollectionProductsOnHover()
				this.closeDrawerAndMenuLinks()

				// Setup debounce events
				const debounceMenuDrawer = debounce(() => {
					this.setMenuDrawerPosition()
				}, 50)

				this.debounceMenuDrawerMouseMove = debounce((menuLinkEl, matchMenuDrawerEL, event) => {
					this.setupCloseMenuOnHoverEvents(menuLinkEl, matchMenuDrawerEL, event)
				}, 20)

				// Setup resize event
				window.addEventListener('resize', debounceMenuDrawer.bind(this))
			}

			setMenuDrawerPosition() {
				this.menuLinkEls.forEach((menuLinkEl) => {
					const menuDrawerId = menuLinkEl.dataset.menuDrawerLink
					const menuDrawerEL = document.getElementById(menuDrawerId)

					if (!menuDrawerEL) return

					// Set view point variables
					updateHeaderCSSVariables()

					// Setup position left
					const menuItemOffsetLeft = menuLinkEl.offsetLeft
					menuDrawerEL.style.setProperty('--l-menu-position-left', menuItemOffsetLeft + 'px')

					// Set max-height
					const menuDrawerChildNodes = menuDrawerEL.querySelectorAll('[data-menu-drawer-group]')
					const firstNode = menuDrawerChildNodes[0]

					const firstNodeHeight = firstNode.scrollHeight
					const windowHeight = visualViewport?.height ?? window.innerHeight

					menuDrawerEL.classList.toggle('is-max-height-first-element', firstNodeHeight < windowHeight)

					if (firstNodeHeight < windowHeight) {
						menuDrawerEL.style.setProperty('--l-menu-drawer-max-height', firstNodeHeight + 'px')
					}
				})
			}

			setupOpenMenuOnHoverEvents() {
				this.menuLinkEls.forEach((menuLinkEl) => {
					const menuDrawerId = menuLinkEl.dataset.menuDrawerLink
					const matchMenuDrawerEL = document.getElementById(menuDrawerId)

					if (!matchMenuDrawerEL) return

					menuLinkEl.addEventListener('mouseenter', () => {
						lockBodyScroll()

						this.menuLinkEls.forEach((currentMenuLinkEl) => {
							currentMenuLinkEl.classList.toggle('is-hover', menuLinkEl == currentMenuLinkEl)
						})

						this.menuDrawerEls.forEach((menuDrawerEl) => {
							menuDrawerEl.classList.toggle('is-hover', menuDrawerEl == matchMenuDrawerEL)
							menuDrawerEl.removeEventListener('mousemove', this.debounceMenuDrawerMouseMove)
						})

						window.addEventListener(
							'mousemove',
							this.debounceMenuDrawerMouseMove.bind(this, menuLinkEl, matchMenuDrawerEL)
						)
					})
				})
			}

			closeDrawerAndMenuLinks() {
				const removeDrawerFn = () => {
					this.menuLinkEls.forEach((menuLinkEl) => {
						menuLinkEl.classList.remove('is-hover')
					})

					this.menuDrawerEls.forEach((menuDrawerEl) => {
						menuDrawerEl.classList.remove('is-hover')
					})
				}

				this.querySelectorAll('a').forEach((linkEl) => {
					linkEl.addEventListener('click', () => {
						removeDrawerFn()
					})
				})

				this.menuDrawerEls.forEach((menuDrawerEl) => {
					menuDrawerEl.querySelectorAll('a').forEach((linkEl) => {
						linkEl.addEventListener('click', () => {
							removeDrawerFn()
						})
					})
				})
			}

			setupCloseMenuOnHoverEvents(menuLinkEl, matchMenuDrawerEL, event) {
				const target = event.target

				if (!menuLinkEl.contains(target) && !matchMenuDrawerEL.contains(target)) {
					menuLinkEl.classList.remove('is-hover')
					matchMenuDrawerEL.classList.remove('is-hover')

					window.removeEventListener('mousemove', this.debounceMenuDrawerMouseMove)

					unLockBodyScroll()
				}
			}

			toggleCollectionProductsOnHover() {
				this.collectionCardEls = document.querySelectorAll('[data-category-card]')
				this.collectionCardProductsEls = document.querySelectorAll('[id^="Categories-"]')

				this.collectionCardEls.forEach((collectionCardEl) => {
					collectionCardEl.addEventListener('mouseenter', () => {
						const collectionProductId = collectionCardEl.dataset.categoryCard
						const collectionProductsEl = document.getElementById(collectionProductId)

						if (!collectionProductsEl) return

						this.collectionCardProductsEls.forEach((collectionCardProductsEl) => {
							collectionCardProductsEl.classList.toggle('is-open', collectionCardProductsEl == collectionProductsEl)
							collectionCardProductsEl.classList.toggle('hidden', collectionCardProductsEl != collectionProductsEl)
						})
					})
				})
			}
		}
	)
}
